import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  useMediaQuery,
  useTheme,
  Drawer,
  IconButton,
  Skeleton,
} from '@mui/material';
import {
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { usePublicBlogs } from '../hooks/usePublicBlogs';
import BlogDetailModal from '../components/Modals/BlogDetailModal';

// ─── Design tokens — HTML reference palette ────────────────────────────────
export const N = {
  navy:       '#0B1D6E',   // dark brand navy — CTA, headings on light
  navyMid:    '#1D4ED8',   // primary blue accent — buttons, labels
  navyLight:  '#2563EB',   // hover blue
  amber:      '#F59E0B',   // warm accent — CTA button, quote marks, stats
  amberLight: '#FCD34D',   // amber hover
  white:      '#FFFFFF',
  blue50:     '#EFF6FF',   // hero right bg, tag pill bg
  blue100:    '#DBEAFE',   // tag/card borders
  gray50:     '#F8FAFC',   // features / testimonials / footer bg
  gray100:    '#F1F5F9',   // progress bar track
  gray200:    '#E2E8F0',   // borders
  gray400:    '#94A3B8',   // footer labels
  gray600:    '#475569',   // secondary text
  gray800:    '#1E293B',   // main text on light bg
  muted:      '#64748B',   // captions / meta
  teal:       '#0D9488',
  coral:      '#DC2626',
  bg:         '#F0F4F8',   // page background
  cardShadow: '0 10px 30px rgba(11,29,110,0.08)',
};

export const formatDateDdMmYyyy = (value: any): string => {
  if (!value) return "";

  if (Array.isArray(value)) {
    const [y, m, d] = value;
    return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
  }

  if (typeof value === "string") {
    if (value.includes(",")) {
      const parts = value.split(",").map(Number);
      if (parts.length >= 3) {
        const [y, m, d] = parts;
        return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
      }
    }
    const dt = new Date(value);
    if (!Number.isNaN(dt.getTime())) {
      const d = dt.getDate();
      const m = dt.getMonth() + 1;
      const y = dt.getFullYear();
      return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
    }
  }

  return "";
};

export const navDropdownPaperSx = {
  background: N.white,
  border: `1px solid ${N.gray200}`,
  borderRadius: '10px',
  mt: 1.5,
  minWidth: 220,
  boxShadow: '0 8px 32px rgba(11,29,110,0.12)',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: `linear-gradient(90deg, ${N.navyMid}, ${N.teal})`,
  },
  '& .MuiMenuItem-root': {
    color: N.gray800,
    py: 1.75,
    px: 2.5,
    fontSize: '0.9rem',
    fontWeight: 500,
    transition: 'background 0.2s',
    '&:hover': { background: N.blue50 },
    '&.Mui-selected': { background: N.blue50, color: N.navyMid },
  },
};

export const navDropdownMenuProps = {
  disableScrollLock: true,
  transformOrigin: { horizontal: 'right' as const, vertical: 'top' as const },
  anchorOrigin: { horizontal: 'right' as const, vertical: 'bottom' as const },
  PaperProps: { sx: navDropdownPaperSx },
};

// ─── iSchoolNext Logo SVG — variant='dark' for light bg, 'light' for dark bg ─
export const Logo: React.FC<{ gradId?: string; width?: number; height?: number; variant?: 'light' | 'dark' }> = ({
  gradId = 'lg1', width = 176, height = 38, variant = 'dark',
}) => {
  const mainFill = variant === 'dark' ? N.navy    : '#FFFFFF';
  const nextFill = variant === 'dark' ? N.navyMid : '#93C5FD';
  const subFill  = variant === 'dark' ? 'rgba(11,29,110,0.38)' : 'rgba(255,255,255,0.28)';
  const lineClr  = variant === 'dark' ? 'rgba(11,29,110,0.12)' : 'rgba(255,255,255,0.1)';
  return (
    <svg width={width} height={height} viewBox="0 0 200 44" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#1D4ED8" />
          <stop offset="100%" stopColor="#0B1D6E" />
        </linearGradient>
      </defs>
      <path d="M20 2 L36 8 L36 22 Q36 34 20 42 Q4 34 4 22 L4 8 Z" fill={`url(#${gradId})`} />
      <path d="M20 6 L32 11 L32 22 Q32 31 20 38 Q8 31 8 22 L8 11 Z"
        fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
      <circle cx="20" cy="14" r="3.5" fill={N.amber} />
      <rect x="17" y="20" width="6" height="14" rx="2" fill="#FFFFFF" />
      <text x="46" y="22" fontFamily="DM Sans,Segoe UI,sans-serif"
        fontWeight="700" fontSize="17" fill={mainFill}>iSchool</text>
      <text x="110" y="22" fontFamily="DM Sans,Segoe UI,sans-serif"
        fontWeight="700" fontSize="17" fill={nextFill}>Next</text>
      <line x1="46" y1="27" x2="163" y2="27" stroke={lineClr} strokeWidth="0.5" />
      <text x="46" y="36" fontFamily="DM Sans,Segoe UI,sans-serif"
        fontWeight="500" fontSize="8" fill={subFill}
        style={{ letterSpacing: '2.5px' }}>SCHOOL ERP</text>
    </svg>
  );
};

// ─── Scroll-reveal wrapper ──────────────────────────────────────────────────
const Reveal: React.FC<{ children: React.ReactNode; delay?: number; sx?: object }> = ({
  children, delay = 0, sx = {},
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [vis, setVis] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } },
      { threshold: 0.1 },
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        opacity: vis ? 1 : 0,
        transform: vis ? 'none' : 'translateY(28px)',
        transition: `opacity 0.7s ease ${delay}ms, transform 0.7s ease ${delay}ms`,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

// ─── Typewriter ─────────────────────────────────────────────────────────────
const Typewriter: React.FC<{ words: string[]; speed?: number; deleteSpeed?: number; pause?: number }> = ({
  words, speed = 95, deleteSpeed = 55, pause = 1300,
}) => {
  const [displayed, setDisplayed] = useState('');
  const [wordIdx,   setWordIdx]   = useState(0);
  const [typing,    setTyping]    = useState(true);

  useEffect(() => {
    const word = words[wordIdx];
    let t: ReturnType<typeof setTimeout>;
    if (typing) {
      if (displayed.length < word.length) {
        t = setTimeout(() => setDisplayed(word.slice(0, displayed.length + 1)), speed);
      } else {
        t = setTimeout(() => setTyping(false), pause);
      }
    } else {
      if (displayed.length > 0) {
        t = setTimeout(() => setDisplayed(word.slice(0, displayed.length - 1)), deleteSpeed);
      } else {
        setWordIdx(i => (i + 1) % words.length);
        setTyping(true);
      }
    }
    return () => clearTimeout(t);
  }, [displayed, typing, wordIdx, words, speed, deleteSpeed, pause]);

  return (
    <Box component="em" sx={{ fontStyle: 'italic', color: N.navyMid }}>
      {displayed || ' '}
    </Box>
  );
};

// ───────────────────────────────────────────────────────────────────────────
const LandingPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate    = useNavigate();
  const theme       = useTheme();
  const isMobile    = useMediaQuery(theme.breakpoints.down('md'));

  const [loginAnchor, setLoginAnchor] = useState<null | HTMLElement>(null);
  const [mobileOpen,  setMobileOpen]  = useState(false);

  // Inject Google Fonts
  useEffect(() => {
    const link = document.createElement('link');
    link.rel  = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,400&family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400&display=swap';
    document.head.appendChild(link);
    return () => { try { document.head.removeChild(link); } catch {} };
  }, []);

  // Hash scroll listener
  useEffect(() => {
    const h = window.location.hash;
    if (h && h.length > 1 && h !== '#/') {
      try {
        const el = document.querySelector(h);
        el?.scrollIntoView({ behavior: 'smooth' });
      } catch (err) {
        // Invalid selector in hash (e.g. "#/" from HashRouter). Ignore.
      }
    }
  }, []);

  const navLinks = [
    { label: t('landing_nav_features'), href: '#features' },
    { label: t('landing_nav_platform'), href: '#modules'  },
    { label: t('landing_nav_schools'),  href: '#schools'  },
    { label: t('landing_nav_blogs'),    href: '#blogs'    },
    { label: t('landing_nav_about'),    href: '#about'    },
  ];

  // ─── Public blogs state ───────────────────────────────────────────────
  const { usePublicBlogsQuery, usePublicBlogBySlugQuery } = usePublicBlogs();
  const publicBlogsQuery = usePublicBlogsQuery(0, 6);
  const [selectedBlogSlug, setSelectedBlogSlug] = useState<string | null>(null);
  const blogDetailQuery = usePublicBlogBySlugQuery(selectedBlogSlug || '', !!selectedBlogSlug);
  const handleBlogClick = useCallback((slug: string) => setSelectedBlogSlug(slug), []);
  const handleBlogModalClose = useCallback(() => setSelectedBlogSlug(null), []);

  const scrollTo = (href: string) => {
    if (!href || href === '#/' ) return;
    try {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (err) {
      // Ignore invalid selectors
    }
  };

  const marqueeItems = [
    t('landing_feature_admission_form'),
    t('landing_feature_marksheet'),
    t('landing_feature_gosvara'),
    t('landing_feature_admit_cards'),
    t('landing_feature_student_management'),
    t('landing_feature_teacher_management'),
    t('landing_feature_aadhar'),
    t('landing_feature_bulk_printing'),
    t('landing_feature_document_management'),
  ];

  const gridFeatures = [
    { num: '01', icon: '🎓', bg: N.blue50, border: N.blue100,
      name: t('landing_feature_admission_form'),
      desc: t('landing_feature_admission_form_desc') },
    { num: '02', icon: '📄', bg: 'rgba(13,148,136,0.12)', border: 'rgba(13,148,136,0.2)',
      name: t('landing_feature_marksheet'),
      desc: t('landing_feature_marksheet_desc') },
    { num: '03', icon: '📋', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.2)',
      name: t('landing_feature_gosvara'),
      desc: t('landing_feature_gosvara_desc') },
    { num: '04', icon: '🪪', bg: 'rgba(220,38,38,0.08)', border: 'rgba(220,38,38,0.15)',
      name: t('landing_feature_admit_cards'),
      desc: t('landing_feature_admit_cards_desc') },
    { num: '05', icon: '👥', bg: 'rgba(90,120,255,0.1)', border: 'rgba(90,120,255,0.2)',
      name: t('landing_feature_student_management'),
      desc: t('landing_feature_student_management_desc') },
    { num: '06', icon: '👨‍🏫', bg: 'rgba(13,148,136,0.08)', border: 'rgba(13,148,136,0.15)',
      name: t('landing_feature_teacher_management'),
      desc: t('landing_feature_teacher_management_desc') },
  ];

  const moduleCards = [
    { thumbBg: N.blue50, thumbBorder: N.blue100, icon: '🪪', level: t('landing_module_level_docs'),
      subject: t('landing_feature_aadhar'),
      desc:    t('landing_feature_aadhar_desc'),
      meta: [t('landing_module_meta_bulk_export'), t('landing_module_meta_verified')] },
    { thumbBg: '#F0FDF4', thumbBorder: '#BBF7D0', icon: '🖨️', level: t('landing_module_level_print'),
      subject: t('landing_feature_bulk_printing'),
      desc:    t('landing_feature_bulk_printing_desc'),
      meta: [t('landing_module_meta_one_click'), t('landing_module_meta_all_formats')] },
    { thumbBg: '#FFF7ED', thumbBorder: '#FED7AA', icon: '📁', level: t('landing_module_level_storage'),
      subject: t('landing_feature_document_management'),
      desc:    t('landing_feature_document_management_desc'),
      meta: [t('landing_module_meta_cloud'), t('landing_module_meta_secure')] },
    { thumbBg: '#FDF4FF', thumbBorder: '#E9D5FF', icon: '📊', level: t('landing_module_level_analytics'),
      subject: t('landing_module_analytics_subject'),
      desc:    t('landing_module_analytics_desc'),
      meta: [t('landing_module_meta_realtime'), t('landing_module_meta_exports')] },
  ];

  const benefits = [
    { icon: '⚡', title: t('landing_benefit_fast'),       desc: t('landing_benefit_fast_desc') },
    { icon: '🔒', title: t('landing_benefit_secure'),     desc: t('landing_benefit_secure_desc') },
    { icon: '🏫', title: t('landing_benefit_comprehensive'), desc: t('landing_benefit_comprehensive_desc') },
  ];

  const stats = [
    { value: '500+', label: 'Schools' },
    { value: '50,000+', label: 'Students' },
    { value: '99.9%', label: 'Uptime' },
    { value: '9+', label: 'Features' },
  ];

  return (
    <Box sx={{ background: N.white, color: N.gray800, minHeight: '100vh', fontFamily: "'DM Sans',sans-serif", overflowX: 'hidden' }}>

      {/* ═══════════════════════════════ NAV ═══════════════════════════════ */}
      <Box
        component="nav"
        sx={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: { xs: 2, md: '4rem' }, py: '1rem',
          background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${N.gray200}`,
          transition: 'background 0.3s',
        }}
      >
        <Box sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <Logo gradId="nav-g" variant="dark" width={isMobile ? 120 : 176} height={isMobile ? 26 : 38} />
        </Box>

        {!isMobile && (
          <Stack direction="row" spacing={4} component="ul"
            sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {navLinks.map(({ label, href }) => (
              <Box component="li" key={label}>
                <Box component="a" href={href}
                  onClick={(e: React.MouseEvent) => { e.preventDefault(); scrollTo(href); }}
                  sx={{
                    color: N.gray600, textDecoration: 'none',
                    fontSize: '0.875rem', fontWeight: 500,
                    letterSpacing: '0.04em', textTransform: 'uppercase',
                    transition: 'color 0.2s', cursor: 'pointer',
                    '&:hover': { color: N.navy },
                  }}>
                  {label}
                </Box>
              </Box>
            ))}
          </Stack>
        )}

        <Stack direction="row" spacing={1.5} alignItems="center">
          <FormControl
            sx={{
              display: { xs: 'none', md: 'block' },
              backgroundColor: N.navy,
              borderRadius: '2px',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: `0 4px 16px rgba(11,29,110,0.25)`,
                '& .MuiOutlinedInput-root': { backgroundColor: N.navyMid },
              },
              '& .MuiOutlinedInput-root': {
                backgroundColor: N.navy, color: N.white,
                borderRadius: '2px', fontWeight: 600, fontSize: '0.8rem',
                letterSpacing: '0.04em', textTransform: 'uppercase',
                transition: 'background-color 0.2s',
                '& .MuiOutlinedInput-notchedOutline': { border: 'none', background: 'transparent' },
                '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none', background: 'transparent' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none', background: 'transparent' },
                '& fieldset': { border: 'none', background: 'transparent' },
                '& .MuiSelect-select': { background: 'transparent' },
              },
              '& .MuiSelect-icon': { color: N.white, background: 'transparent' },
              '& .MuiSelect-select': { py: '0.55rem', px: '1.25rem', lineHeight: 1.75 },
            }}
          >
            <Select
              value={i18n.language}
              onChange={(e) => {
                const lang = e.target.value;
                i18n.changeLanguage(lang);
                try { localStorage.setItem('selectedLanguage', lang); localStorage.setItem('i18nextLng', lang); } catch {}
              }}
              MenuProps={navDropdownMenuProps}
            >
              <MenuItem value="hi">हिन्दी</MenuItem>
              <MenuItem value="en">English</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            onClick={(e) => setLoginAnchor(e.currentTarget)}
            sx={{
              background: N.navy, color: N.white,
              fontWeight: 600, fontSize: '0.875rem',
              px: { xs: 1.5, md: '1.5rem' }, py: '0.485rem',
              borderRadius: '2px', letterSpacing: '0.04em',
              textTransform: 'uppercase', minWidth: 0,
              transition: 'all 0.2s',
              '&:hover': {
                background: N.navyMid,
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 16px rgba(11,29,110,0.25)',
              },
            }}
          >
            {t('landing_login')}
          </Button>

          <Menu
            anchorEl={loginAnchor}
            open={Boolean(loginAnchor)}
            onClose={() => setLoginAnchor(null)}
            disableScrollLock
            PaperProps={{ sx: navDropdownPaperSx }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem
              onClick={() => { setLoginAnchor(null); navigate('/login?type=student'); }}
              sx={{
                color: N.gray800, py: 1.75, px: 2.5, gap: 1.5,
                transition: 'background 0.2s',
                '&:hover': { background: N.blue50 },
              }}
            >
              <PersonIcon sx={{ color: N.gray400, fontSize: 20 }} />
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>{t('landing_student_login')}</Typography>
            </MenuItem>
            <Box sx={{ height: '1px', background: N.gray200, mx: 1 }} />
            <MenuItem
              onClick={() => { setLoginAnchor(null); navigate('/login?type=staff'); }}
              sx={{
                color: N.gray800, py: 1.75, px: 2.5, gap: 1.5,
                transition: 'background 0.2s',
                '&:hover': { background: N.blue50 },
              }}
            >
              <AdminIcon sx={{ color: N.gray400, fontSize: 20 }} />
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>{t('landing_staff_login')}</Typography>
            </MenuItem>
          </Menu>

          {isMobile && (
            <IconButton onClick={() => setMobileOpen(true)}
              sx={{ color: N.gray600, p: 0.5 }}>
              <MenuIcon />
            </IconButton>
          )}
        </Stack>
      </Box>

      {/* Mobile drawer */}
      <Drawer anchor="right" open={mobileOpen} onClose={() => setMobileOpen(false)}
        PaperProps={{ sx: { background: N.navy, color: N.white, width: 260, p: 3 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Logo gradId="mob-g" variant="light" width={140} height={30} />
          <IconButton onClick={() => setMobileOpen(false)} sx={{ color: 'rgba(255,255,255,0.6)' }}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Stack spacing={0.5}>
          {navLinks.map(({ label, href }) => (
            <Box key={label} component="a" href={href}
              onClick={(e: React.MouseEvent) => { e.preventDefault(); setMobileOpen(false); scrollTo(href); }}
              sx={{
                display: 'block', color: 'rgba(255,255,255,0.75)', textDecoration: 'none',
                py: 1.5, px: 2, borderRadius: 1,
                fontSize: '0.9rem', fontWeight: 500,
                textTransform: 'uppercase', letterSpacing: '0.05em',
                transition: 'all 0.2s', cursor: 'pointer',
                '&:hover': { color: N.white, background: 'rgba(255,255,255,0.08)' },
              }}
            >
              {label}
            </Box>
          ))}
          <Box sx={{ pt: 2, mt: 1, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <FormControl fullWidth size="small" sx={{
              mb: 1.5,
              backgroundColor: 'rgba(255,255,255,0.1)',
              borderRadius: '2px',
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'transparent', color: N.white,
                borderRadius: '2px', fontWeight: 600, fontSize: '0.85rem',
                '& .MuiOutlinedInput-notchedOutline': { border: 'none', background: 'transparent' },
                '& fieldset': { border: 'none', background: 'transparent' },
                '& .MuiSelect-select': { background: 'transparent' },
              },
              '& .MuiSelect-icon': { color: N.white, background: 'transparent' },
            }}>
              <Select
                value={i18n.language}
                onChange={(e) => {
                  const lang = e.target.value;
                  i18n.changeLanguage(lang);
                  try { localStorage.setItem('selectedLanguage', lang); localStorage.setItem('i18nextLng', lang); } catch {}
                }}
                MenuProps={navDropdownMenuProps}
              >
                <MenuItem value="hi">हिन्दी</MenuItem>
                <MenuItem value="en">English</MenuItem>
              </Select>
            </FormControl>
            <Button fullWidth variant="contained"
              onClick={() => { setMobileOpen(false); navigate('/contact-us'); }}
              sx={{
                background: N.amber, color: N.navy, fontWeight: 700,
                borderRadius: '2px', textTransform: 'none', py: 1.2,
                '&:hover': { background: N.amberLight },
              }}
            >
              {t('landing_mobile_book_demo')}
            </Button>
          </Box>
        </Stack>
      </Drawer>

      {/* ══════════════════════════════ HERO ═══════════════════════════════ */}
      <Box
        component="section"
        sx={{
          minHeight: 'calc(100vh - 56px)',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          position: 'relative', overflow: 'hidden',
        }}
      >
        {/* Left */}
        <Box
          sx={{
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
            pt: { xs: '80px', md: '5rem' }, pb: { xs: 4, md: '1.5rem' },
            px: { xs: 3, sm: 4, md: '4rem' },
            position: 'relative', zIndex: 2,
          }}
        >
          {/* Tag pill */}
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 1,
            fontFamily: "'DM Mono',monospace", fontSize: '0.72rem',
            color: N.navyMid, letterSpacing: '0.12em', textTransform: 'uppercase',
            border: `1px solid ${N.blue100}`, background: N.blue50,
            px: '1rem', py: '0.375rem', borderRadius: '2px',
            width: 'fit-content', mb: 2,
            animation: 'slideTag 0.6s ease-out both',
            '@keyframes slideTag': { from: { opacity: 0, transform: 'translateX(-20px)' }, to: { opacity: 1, transform: 'none' } },
          }}>
            <Box component="span" sx={{
              width: 6, height: 6, borderRadius: '50%',
              background: N.amber, display: 'inline-block',
              animation: 'dot 2s ease-in-out infinite',
              '@keyframes dot': { '0%,100%': { opacity: 1, transform: 'scale(1)' }, '50%': { opacity: 0.4, transform: 'scale(0.8)' } },
            }} />
            {t('landing_school_management_system')}
          </Box>

          {/* Headline */}
          <Typography component="h1" sx={{
            fontFamily: "'Playfair Display',serif",
            fontSize: { xs: '2.4rem', sm: '3rem', md: 'clamp(2.5rem,4vw,4.5rem)' },
            fontWeight: 900, lineHeight: 1.02,
            letterSpacing: '-0.03em', mb: '1rem', color: N.navy,
            animation: 'fadeUp 0.7s ease-out 0.1s both',
            '@keyframes fadeUp': { from: { opacity: 0, transform: 'translateY(20px)' }, to: { opacity: 1, transform: 'none' } },
          }}>
            {t('landing_hero_headline')} <br />
            <Typewriter words={[t('landing_hero_word_1'), t('landing_hero_word_2'), t('landing_hero_word_3'), t('landing_hero_word_4')]} />
          </Typography>

          {/* Sub */}
          <Typography sx={{
            fontSize: { xs: '0.95rem', md: '1rem' },
            color: N.gray600, lineHeight: 1.65,
            maxWidth: 440, mb: '1.25rem',
            animation: 'fadeUp 0.7s ease-out 0.2s both',
          }}>
            {t('landing_hero_sub')}
          </Typography>

          {/* Actions */}
          <Stack direction="row" spacing={2} alignItems="center"
            sx={{ animation: 'fadeUp 0.7s ease-out 0.3s both' }}>
            <Button variant="contained" onClick={() => navigate('/contact-us')}
              sx={{
                background: N.navy, color: N.white,
                fontWeight: 700, fontSize: '0.95rem',
                px: '2.25rem', py: '1rem', borderRadius: '2px',
                letterSpacing: '0.02em', textTransform: 'none', transition: 'all 0.2s',
                '&:hover': { background: N.navyMid, transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(11,29,110,0.2)' },
              }}
            >
              {t('landing_book_demo')}
            </Button>
            <Box component="a" href="#features" sx={{
              color: N.gray600, fontSize: '0.875rem', fontWeight: 500,
              textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 0.75,
              transition: 'color 0.2s',
              '&:hover': { color: N.navy },
              '&:hover .arr': { transform: 'translateX(4px)' },
            }}>
              {t('landing_see_how')}
              <Box component="span" className="arr" sx={{ transition: 'transform 0.2s', display: 'inline-block' }}>→</Box>
            </Box>
          </Stack>

          {/* Stats */}
          <Stack direction="row" flexWrap="wrap"
            sx={{
              mt: '1.5rem', pt: '1.25rem',
              borderTop: `1px solid ${N.gray200}`,
              gap: { xs: 3, md: 4 },
              animation: 'fadeUp 0.7s ease-out 0.4s both',
            }}
          >
            {[
              { val: '30+', label: t('landing_stat_schools') },
              { val: '18',  label: t('landing_stat_modules') },
              { val: '60%', label: t('landing_stat_time')    },
            ].map(s => (
              <Box key={s.label}>
                <Typography sx={{ fontFamily: "'Playfair Display',serif", fontSize: '2rem', fontWeight: 700, color: N.navy, lineHeight: 1 }}>
                  {s.val}
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', color: N.muted, letterSpacing: '0.06em', textTransform: 'uppercase', mt: '2px' }}>
                  {s.label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* Right — visual panel (desktop only) */}
        {!isMobile && (
          <Box sx={{
            position: 'relative', background: N.blue50,
            overflow: 'hidden', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            borderLeft: `1px solid ${N.gray200}`,
            perspective: '1400px',
          }}>
            {/* Grid */}
            <Box sx={{
              position: 'absolute', inset: 0, opacity: 0.4,
              backgroundImage: [
                `linear-gradient(${N.gray200} 1px, transparent 1px)`,
                `linear-gradient(90deg, ${N.gray200} 1px, transparent 1px)`,
              ].join(','),
              backgroundSize: '48px 48px',
            }} />

            {/* Pulsing orbs */}
            <Box sx={{
              position: 'absolute', width: 440, height: 440, borderRadius: '50%',
              filter: 'blur(72px)',
              background: 'radial-gradient(circle, rgba(29,78,216,0.13) 0%, transparent 70%)',
              top: -60, right: -100,
              animation: 'orbPulse 9s ease-in-out infinite',
              '@keyframes orbPulse': {
                '0%,100%': { opacity: 0.6, transform: 'scale(1)' },
                '50%':     { opacity: 1,   transform: 'scale(1.18)' },
              },
            }} />
            <Box sx={{
              position: 'absolute', width: 320, height: 320, borderRadius: '50%',
              filter: 'blur(72px)',
              background: 'radial-gradient(circle, rgba(245,158,11,0.11) 0%, transparent 70%)',
              bottom: 60, left: 20,
              animation: 'orbPulse 9s ease-in-out infinite', animationDelay: '-4.5s',
            }} />

            {/* Cards container */}
            <Box sx={{ position: 'relative', zIndex: 2, width: 360 }}>

              {/* Live badge */}
              <Box sx={{
                position: 'absolute', top: -44, right: 0,
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(10px)',
                border: `1px solid ${N.gray200}`,
                borderRadius: '100px', px: '0.75rem', py: '0.3rem',
                boxShadow: '0 2px 12px rgba(11,29,110,0.08)',
                animation: 'badgeDrop 0.5s cubic-bezier(0.34,1.56,0.64,1) 0.3s both',
                '@keyframes badgeDrop': {
                  from: { opacity: 0, transform: 'translateY(-14px) scale(0.9)' },
                  to:   { opacity: 1, transform: 'none' },
                },
              }}>
                <Box sx={{
                  width: 7, height: 7, borderRadius: '50%', background: '#22C55E', flexShrink: 0,
                  animation: 'liveDot 2s ease-in-out infinite',
                  '@keyframes liveDot': {
                    '0%,100%': { boxShadow: '0 0 0 0 rgba(34,197,94,0.5)' },
                    '60%':     { boxShadow: '0 0 0 5px rgba(34,197,94,0)'  },
                  },
                }} />
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: N.gray600, letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
                  {t('landing_live_badge')}
                </Typography>
              </Box>

              {/* Main card */}
              <Box sx={{
                background: N.white,
                border: `1px solid rgba(29,78,216,0.1)`,
                borderRadius: '14px', p: '1.25rem 1.5rem',
                boxShadow: '0 8px 32px rgba(11,29,110,0.09), 0 1px 4px rgba(11,29,110,0.06)',
                mb: 2,
                transformStyle: 'preserve-3d',
                animation: 'enterA 0.65s cubic-bezier(0.34,1.56,0.64,1) 0.05s both, floatA 7s ease-in-out 0.7s infinite',
                '@keyframes enterA': {
                  from: { opacity: 0, transform: 'translateX(48px) rotateY(18deg) rotateX(4deg)' },
                  to:   { opacity: 1, transform: 'translateX(0)   rotateY(0deg)  rotateX(0deg)' },
                },
                '@keyframes floatA': {
                  '0%,100%': { transform: 'translateY(0px)   rotateX(1.5deg)  rotateY(-1.5deg)' },
                  '50%':     { transform: 'translateY(-12px) rotateX(-1.5deg) rotateY(1.5deg)'  },
                },
                transition: 'box-shadow 0.3s',
                '&:hover': { boxShadow: '0 20px 56px rgba(11,29,110,0.14), 0 4px 16px rgba(11,29,110,0.08)' },
              }}>
                <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '8px', background: 'rgba(245,158,11,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>💰</Box>
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.88rem', color: N.gray800 }}>{t('landing_fee_card_title')}</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: N.muted, mt: '2px' }}>{t('landing_fee_card_school')}</Typography>
                  </Box>
                </Stack>
                {[
                  { label: t('landing_fee_card_bar1'), pct: '84%', c: N.amber, w: '84%', bg: `linear-gradient(90deg,${N.navyMid},${N.amber})`, delay: '0.75s' },
                  { label: t('landing_fee_card_bar2'), pct: '96%', c: N.teal,  w: '96%', bg: `linear-gradient(90deg,${N.teal},#00e5c8)`,       delay: '0.95s' },
                ].map((bar, i) => (
                  <Box key={i} sx={{ mt: i > 0 ? 1.5 : 0 }}>
                    <Box sx={{ background: N.gray100, borderRadius: '100px', height: 6, mb: '0.4rem', overflow: 'hidden' }}>
                      <Box sx={{
                        height: '100%', borderRadius: '100px', background: bar.bg, width: bar.w,
                        transformOrigin: 'left center',
                        animation: `barFill 1.1s cubic-bezier(0.4,0,0.2,1) ${bar.delay} both`,
                        '@keyframes barFill': { from: { transform: 'scaleX(0)' }, to: { transform: 'scaleX(1)' } },
                      }} />
                    </Box>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ fontSize: '0.68rem', color: N.muted }}>{bar.label}</Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: bar.c, fontWeight: 600 }}>{bar.pct}</Typography>
                    </Stack>
                  </Box>
                ))}
              </Box>

              {/* Secondary card */}
              <Box sx={{
                background: N.white,
                border: `1px solid rgba(29,78,216,0.1)`,
                borderRadius: '14px', p: '1.25rem 1.5rem',
                boxShadow: '0 8px 32px rgba(11,29,110,0.09), 0 1px 4px rgba(11,29,110,0.06)',
                ml: '2.5rem',
                transformStyle: 'preserve-3d',
                animation: 'enterB 0.65s cubic-bezier(0.34,1.56,0.64,1) 0.28s both, floatB 7s ease-in-out 0.7s infinite',
                '@keyframes enterB': {
                  from: { opacity: 0, transform: 'translateX(48px) translateY(20px) rotateY(14deg) rotateX(-3deg)' },
                  to:   { opacity: 1, transform: 'none' },
                },
                '@keyframes floatB': {
                  '0%,100%': { transform: 'translateY(0px)  rotateX(-1.5deg) rotateY(1.5deg)'  },
                  '50%':     { transform: 'translateY(12px) rotateX(1.5deg)  rotateY(-1.5deg)' },
                },
                transition: 'box-shadow 0.3s',
                '&:hover': { boxShadow: '0 20px 56px rgba(11,29,110,0.14), 0 4px 16px rgba(11,29,110,0.08)' },
              }}>
                <Typography sx={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: N.muted, mb: 1 }}>
                  {t('landing_pending_title')}
                </Typography>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.25}>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.88rem', color: N.gray800 }}>
                    {t('landing_pending_admissions')}
                  </Typography>
                  <Box sx={{
                    width: 8, height: 8, borderRadius: '50%', background: N.amber,
                    animation: 'amberBlink 2.2s ease-in-out infinite',
                    '@keyframes amberBlink': {
                      '0%,100%': { opacity: 1, transform: 'scale(1)'   },
                      '50%':     { opacity: 0.25, transform: 'scale(0.65)' },
                    },
                  }} />
                </Stack>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: '6px' }}>
                  {[
                    { label: t('landing_tag_fees'), bg: 'rgba(245,158,11,0.1)',  c: '#92400E', delay: '1.0s' },
                    { label: t('landing_tag_hr'),   bg: 'rgba(13,148,136,0.1)', c: '#0F766E', delay: '1.1s' },
                    { label: t('landing_tag_exam'), bg: 'rgba(220,38,38,0.08)', c: '#B91C1C', delay: '1.2s' },
                  ].map(b => (
                    <Box key={b.label} sx={{
                      background: b.bg, color: b.c,
                      fontSize: '0.68rem', fontWeight: 500,
                      px: '0.6rem', py: '0.25rem',
                      borderRadius: '100px', letterSpacing: '0.04em',
                      animation: `tagPop 0.4s cubic-bezier(0.34,1.56,0.64,1) ${b.delay} both`,
                      '@keyframes tagPop': {
                        from: { opacity: 0, transform: 'scale(0.7)' },
                        to:   { opacity: 1, transform: 'scale(1)'   },
                      },
                    }}>
                      {b.label}
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* ══════════════════════════ MARQUEE STRIP ═══════════════════════════ */}
      <Box sx={{ background: N.navy, py: '0.875rem', overflow: 'hidden' }}>
        <Box sx={{
          display: 'flex', gap: '3.5rem',
          animation: 'marquee 28s linear infinite',
          whiteSpace: 'nowrap', width: 'max-content',
          '@keyframes marquee': { from: { transform: 'translateX(0)' }, to: { transform: 'translateX(-50%)' } },
        }}>
          {[...marqueeItems, ...marqueeItems].map((item, i) => (
            <Box key={i} component="span" sx={{
              fontFamily: "'Playfair Display',serif",
              fontWeight: 700, fontSize: '0.875rem',
              color: 'rgba(255,255,255,0.7)', letterSpacing: '0.06em',
              textTransform: 'uppercase',
              display: 'inline-flex', alignItems: 'center', gap: '0.9rem',
              '&::after': { content: '"✦"', fontSize: '0.6rem', opacity: 0.5, color: N.amber },
            }}>
              {item}
            </Box>
          ))}
        </Box>
      </Box>

      {/* ══════════════════════════ FEATURES ════════════════════════════════ */}
      <Box
        id="features"
        component="section"
        sx={{ py: { xs: '4rem', md: '7rem' }, background: N.gray50, color: N.navy }}
      >
        <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, sm: 3, md: '4rem' } }}>
          <Reveal>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: { xs: 3, md: '5rem' },
              alignItems: 'end', mb: { xs: 4, md: '5rem' },
            }}>
              <Box>
                <Typography sx={{
                  fontFamily: "'DM Mono',monospace", fontSize: '0.7rem',
                  color: N.navyMid, letterSpacing: '0.14em',
                  textTransform: 'uppercase', mb: 1.5,
                }}>
                  {t('landing_features_label')}
                </Typography>
                <Typography component="h2" sx={{
                  fontFamily: "'Playfair Display',serif",
                  fontSize: { xs: '2rem', md: 'clamp(2rem,3.5vw,3.5rem)' },
                  fontWeight: 900, lineHeight: 1.08,
                  letterSpacing: '-0.03em', color: N.navy,
                }}>
                  {t('landing_useful_features')}<br />
                  <Box component="em" sx={{ fontStyle: 'italic', color: N.navyMid }}>{t('landing_features_heading_2')}</Box>
                </Typography>
              </Box>
              <Typography sx={{
                fontSize: '1.05rem', color: N.gray600,
                lineHeight: 1.75, maxWidth: 420, alignSelf: 'end',
              }}>
                {t('landing_features_sub')}
              </Typography>
            </Box>
          </Reveal>

          {/* Features grid */}
          <Reveal delay={100}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3,1fr)' },
              gap: '1px',
              background: N.gray200,
              border: `1px solid ${N.gray200}`,
              borderRadius: '8px', overflow: 'hidden',
            }}>
              {gridFeatures.map((f, i) => (
                <Box key={i} sx={{
                  background: N.white, p: { xs: '2rem', md: '2.5rem' },
                  cursor: 'default', transition: 'background 0.3s',
                  '&:hover': { background: N.blue50 },
                }}>
                  <Typography sx={{
                    fontFamily: "'Playfair Display',serif",
                    fontSize: '3.5rem', fontWeight: 900,
                    color: N.gray200, lineHeight: 1, mb: 2,
                  }}>
                    {f.num}
                  </Typography>
                  <Box sx={{
                    width: 48, height: 48, borderRadius: '8px',
                    background: f.bg, border: `1px solid ${f.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.25rem', mb: 2,
                  }}>
                    {f.icon}
                  </Box>
                  <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, mb: '0.6rem', color: N.navy }}>
                    {f.name}
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: N.gray600, lineHeight: 1.7 }}>
                    {f.desc}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Reveal>
        </Box>
      </Box>

      {/* ══════════════════════════ BENEFITS BAR ════════════════════════════ */}
      <Box sx={{ background: N.navy, py: { xs: 4, md: 5 } }}>
        <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, md: '4rem' } }}>
          <Reveal>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3,1fr)' },
              gap: { xs: 3, md: '2rem' },
            }}>
              {benefits.map((b, i) => (
                <Box key={i} sx={{
                  display: 'flex', alignItems: 'flex-start', gap: 2,
                  p: '1.5rem', borderRadius: '12px',
                  border: '1px solid rgba(255,255,255,0.08)',
                  background: 'rgba(255,255,255,0.03)',
                  transition: 'border-color 0.3s, transform 0.3s',
                  '&:hover': { borderColor: `rgba(245,158,11,0.35)`, transform: 'translateY(-3px)' },
                }}>
                  <Box sx={{ fontSize: '1.75rem', flexShrink: 0 }}>{b.icon}</Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.5, color: N.amber }}>
                      {b.title}
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.6 }}>
                      {b.desc}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Reveal>
        </Box>
      </Box>

      {/* ══════════════════════════ MODULES ══════════════════════════════════ */}
      <Box id="modules" component="section"
        sx={{ py: { xs: '4rem', md: '7rem' }, background: N.white }}>
        <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, sm: 3, md: '4rem' } }}>
          <Reveal>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'flex-end' }} mb={5}>
              <Box>
                <Typography sx={{
                  fontFamily: "'DM Mono',monospace", fontSize: '0.7rem',
                  color: N.navyMid, letterSpacing: '0.14em',
                  textTransform: 'uppercase', mb: 1.5,
                }}>
                  {t('landing_why_choose_us')}
                </Typography>
                <Typography component="h2" sx={{
                  fontFamily: "'Playfair Display',serif",
                  fontSize: { xs: '2rem', md: 'clamp(2rem,3.5vw,3.5rem)' },
                  fontWeight: 900, lineHeight: 1.08, letterSpacing: '-0.03em', color: N.navy,
                }}>
                  {t('landing_platform_heading_1')}<br />
                  <Box component="em" sx={{ fontStyle: 'italic', color: N.navyMid }}>{t('landing_platform_heading_2')}</Box>
                </Typography>
              </Box>
            </Stack>
          </Reveal>

          <Reveal delay={100}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4,1fr)' },
              gap: '1.25rem',
            }}>
              {moduleCards.map((c, i) => (
                <Box key={i} sx={{
                  background: N.white, border: `1px solid ${N.gray200}`,
                  borderRadius: '12px', overflow: 'hidden', cursor: 'pointer',
                  transition: 'border-color 0.3s, transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    borderColor: N.navyMid,
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 32px rgba(11,29,110,0.1)',
                  },
                }}>
                  <Box sx={{
                    height: 140, position: 'relative', overflow: 'hidden',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '2.5rem', background: c.thumbBg,
                    borderBottom: `1px solid ${c.thumbBorder}`,
                  }}>
                    <Box sx={{
                      position: 'absolute', top: '1rem', left: '1rem',
                      fontSize: '0.62rem', fontWeight: 600, letterSpacing: '0.1em',
                      textTransform: 'uppercase', px: '0.6rem', py: '0.25rem',
                      borderRadius: '2px',
                      background: 'rgba(11,29,110,0.08)', color: N.navy,
                    }}>
                      {c.level}
                    </Box>
                    {c.icon}
                  </Box>
                  <Box sx={{ p: '1.25rem' }}>
                    <Typography sx={{
                      fontSize: '0.68rem', fontWeight: 500,
                      letterSpacing: '0.08em', textTransform: 'uppercase',
                      color: N.navyMid, mb: 0.75,
                    }}>
                      {c.subject}
                    </Typography>
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, lineHeight: 1.4, mb: 1, color: N.gray800 }}>
                      {c.desc}
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ fontSize: '0.72rem', color: N.muted }}>
                      {c.meta.map((m, j) => (
                        <Box key={j} component="span">{j > 0 ? '· ' : ''}{m}</Box>
                      ))}
                    </Stack>
                  </Box>
                </Box>
              ))}
            </Box>
          </Reveal>
        </Box>
      </Box>

      {/* ══════════════════════════ TESTIMONIALS ════════════════════════════ */}
      <Box id="schools" component="section"
        sx={{ py: { xs: '4rem', md: '7rem' }, background: N.gray50, color: N.gray800 }}>
        <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, sm: 3, md: '4rem' } }}>
          <Reveal>
            <Typography sx={{
              fontFamily: "'DM Mono',monospace", fontSize: '0.7rem',
              color: N.navyMid, letterSpacing: '0.14em',
              textTransform: 'uppercase', mb: 1.5,
            }}>
              {t('landing_testimonials_label')}
            </Typography>
            <Typography component="h2" sx={{
              fontFamily: "'Playfair Display',serif",
              fontSize: { xs: '2rem', md: 'clamp(2rem,3.5vw,3.5rem)' },
              fontWeight: 900, lineHeight: 1.08,
              letterSpacing: '-0.03em', color: N.navy,
            }}>
              {t('landing_testimonials_heading_1')}<br />
              <Box component="em" sx={{ fontStyle: 'italic', color: N.navyMid }}>{t('landing_testimonials_heading_2')}</Box>
            </Typography>
          </Reveal>

          <Reveal delay={120}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' },
              gap: 3, mt: 5,
            }}>
              {/* Main testimonial */}
              <Box sx={{
                background: N.navy, color: N.white, borderRadius: '16px',
                p: { xs: '2rem', md: '3rem' },
                display: 'flex', flexDirection: 'column',
                justifyContent: 'space-between', minHeight: 340,
              }}>
                <Box>
                  <Typography component="span" sx={{
                    fontFamily: "'Playfair Display',serif",
                    fontSize: '5rem', lineHeight: 0.6,
                    color: N.amber, mb: 3, display: 'block',
                  }}>"</Typography>
                  <Typography sx={{
                    fontFamily: "'Playfair Display',serif",
                    fontSize: { xs: '1.1rem', md: '1.45rem' },
                    fontWeight: 400, lineHeight: 1.55,
                    fontStyle: 'italic', mb: 3,
                  }}>
                    {t('landing_testimonial_main')}
                  </Typography>
                </Box>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box sx={{
                    width: 48, height: 48, borderRadius: '50%',
                    background: `linear-gradient(135deg,${N.amber},${N.teal})`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: '1rem', color: N.white,
                  }}>RK</Box>
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>{t('landing_testimonial_main_name')}</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.55)', mt: '2px' }}>
                      {t('landing_testimonial_main_role')}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              {/* Mini testimonials */}
              <Stack spacing={2}>
                {[
                  { text: t('landing_testimonial_1'), name: t('landing_testimonial_1_name'), school: t('landing_testimonial_1_role') },
                  { text: t('landing_testimonial_2'), name: t('landing_testimonial_2_name'), school: t('landing_testimonial_2_role') },
                ].map((m, i) => (
                  <Box key={i} sx={{
                    background: N.white, border: `1px solid ${N.gray200}`,
                    borderRadius: '12px', p: '1.5rem', flex: 1,
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 8px 24px rgba(11,29,110,0.08)' },
                  }}>
                    <Typography sx={{ color: N.amber, fontSize: '0.85rem', mb: 1.25, letterSpacing: '0.1em' }}>
                      ★ ★ ★ ★ ★
                    </Typography>
                    <Typography sx={{ fontSize: '0.88rem', color: N.gray600, lineHeight: 1.65, mb: 1.5 }}>
                      {m.text}
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: N.navy }}>{m.name}</Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: N.muted }}>{m.school}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Reveal>
        </Box>
      </Box>

      {/* ════════════════════════════ CTA ═══════════════════════════════════ */}
      <Box component="section"
        sx={{ background: N.navy, py: { xs: '4rem', md: '7rem' }, position: 'relative', overflow: 'hidden' }}>
        <Typography sx={{
          position: 'absolute', fontFamily: "'Playfair Display',serif",
          fontSize: '22vw', fontWeight: 900, color: 'rgba(255,255,255,0.03)',
          top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
          whiteSpace: 'nowrap', pointerEvents: 'none', userSelect: 'none',
        }}>
          MANAGE
        </Typography>
        <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, md: '4rem' } }}>
          <Reveal>
            <Box sx={{ position: 'relative', zIndex: 2, textAlign: 'center', maxWidth: 700, mx: 'auto', color: N.white }}>
              <Typography sx={{
                fontFamily: "'DM Mono',monospace", fontSize: '0.7rem',
                color: 'rgba(255,255,255,0.45)', letterSpacing: '0.14em',
                textTransform: 'uppercase', mb: 1.5,
              }}>
                {t('landing_cta_label')}
              </Typography>
              <Typography component="h2" sx={{
                fontFamily: "'Playfair Display',serif",
                fontSize: { xs: '2.5rem', md: 'clamp(2.5rem,5vw,5rem)' },
                fontWeight: 900, lineHeight: 1.05,
                letterSpacing: '-0.03em', mb: 2, color: N.white,
              }}>
                {t('landing_cta_title')}
              </Typography>
              <Typography sx={{
                fontSize: '1.05rem', color: 'rgba(255,255,255,0.6)',
                lineHeight: 1.7, mb: '2.5rem',
              }}>
                {t('landing_cta_description')}
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/contact-us')}
                sx={{
                  background: N.amber, color: N.navy,
                  fontWeight: 700, fontSize: '0.95rem',
                  px: '2.5rem', py: '1rem', borderRadius: '2px',
                  letterSpacing: '0.02em', textTransform: 'none',
                  transition: 'all 0.2s',
                  '&:hover': {
                    background: N.amberLight,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                  },
                }}
              >
                {t('landing_book_free_demo')}
              </Button>
            </Box>
          </Reveal>
        </Box>
      </Box>

      {/* ══════════════════════════ BLOGS ═════════════════════════════════ */}
      <Box
        id="blogs"
        component="section"
        sx={{ py: { xs: '4rem', md: '7rem' }, background: N.white, color: N.navy }}
      >
        <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, sm: 3, md: '4rem' } }}>
          <Reveal>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: { xs: 3, md: '5rem' },
              alignItems: 'end', mb: { xs: 4, md: '5rem' },
            }}>
              <Box>
                <Typography sx={{
                  fontFamily: "'DM Mono',monospace", fontSize: '0.7rem',
                  color: N.navyMid, letterSpacing: '0.14em',
                  textTransform: 'uppercase', mb: 1.5,
                }}>
                  {t('landing_blogs_label')}
                </Typography>
                <Typography component="h2" sx={{
                  fontFamily: "'Playfair Display',serif",
                  fontSize: { xs: '2rem', md: 'clamp(2rem,3.5vw,3.5rem)' },
                  fontWeight: 900, lineHeight: 1.08,
                  letterSpacing: '-0.03em', color: N.navy,
                }}>
                  {t('landing_blogs_heading_1')}<br />
                  <Box component="em" sx={{ fontStyle: 'italic', color: N.navyMid }}>{t('landing_blogs_heading_2')}</Box>
                </Typography>
              </Box>
              <Typography sx={{
                fontSize: '1.05rem', color: N.gray600,
                lineHeight: 1.75, maxWidth: 420, alignSelf: 'end',
              }}>
                {t('landing_blogs_sub')}
              </Typography>
            </Box>
          </Reveal>

          <Reveal delay={100}>
            {publicBlogsQuery.isLoading ? (
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3,1fr)' },
                gap: '1.5rem',
              }}>
                {[0, 1, 2].map((i) => (
                  <Box key={i} sx={{ borderRadius: '14px', overflow: 'hidden', border: `1px solid ${N.gray200}` }}>
                    <Skeleton variant="rectangular" height={180} animation="wave" />
                    <Box sx={{ p: 2.5 }}>
                      <Skeleton width="40%" height={24} sx={{ mb: 1 }} />
                      <Skeleton width="90%" height={28} sx={{ mb: 1 }} />
                      <Skeleton width="100%" height={18} />
                      <Skeleton width="80%" height={18} />
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : !publicBlogsQuery.data?.content?.length ? (
              <Box sx={{
                textAlign: 'center', py: 6,
                background: N.gray50, borderRadius: '14px',
                border: `1px solid ${N.gray200}`,
              }}>
                <Typography sx={{ fontSize: '2.5rem', mb: 1 }}>📝</Typography>
                <Typography sx={{ fontSize: '1.05rem', color: N.gray600, fontWeight: 500 }}>
                  {t('landing_blogs_empty_state')}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ position: 'relative' }}>
              <Box sx={{
                overflowX: 'auto',
                overflowY: 'visible',
                pb: 2, pt: 1, px: 1, mx: -1,
                scrollBehavior: 'smooth',
                '&::-webkit-scrollbar': { height: 6 },
                '&::-webkit-scrollbar-track': {
                  background: N.gray100, borderRadius: 3,
                },
                '&::-webkit-scrollbar-thumb': {
                  background: N.gray400, borderRadius: 3,
                  '&:hover': { background: N.navyMid },
                },
              }}>
              <Box sx={{
                display: 'flex',
                gap: '1.5rem',
                minWidth: 'min-content',
              }}>
                {publicBlogsQuery.data.content.map((blog) => (
                  <Box
                    key={blog.id}
                    onClick={() => handleBlogClick(blog.slug)}
                    sx={{
                      background: N.white,
                      border: `1px solid ${N.gray200}`,
                      borderRadius: '14px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      flex: '0 0 auto',
                      width: { xs: '85vw', sm: '44vw', md: 360 },
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: N.cardShadow,
                        borderColor: N.blue100,
                      },
                    }}
                  >
                    {blog.coverImageUrl ? (
                      <Box
                        component="img"
                        src={blog.coverImageUrl}
                        alt={blog.title}
                        sx={{
                          width: '100%',
                          height: 190,
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                    ) : (
                      <Box sx={{
                        width: '100%', height: 190,
                        background: `linear-gradient(135deg, ${N.blue50}, ${N.gray50})`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '3rem', color: N.gray200,
                      }}>
                        📰
                      </Box>
                    )}
                    <Box sx={{ p: 2.5 }}>
                      <Box sx={{
                        display: 'inline-block',
                        background: N.blue50,
                        color: N.navyMid,
                        fontSize: '0.68rem',
                        fontWeight: 600,
                        px: '0.7rem', py: '0.2rem',
                        borderRadius: '100px',
                        mb: 1.5,
                        letterSpacing: '0.02em',
                      }}>
                        {blog.category}
                      </Box>
                      <Typography sx={{
                        fontFamily: "'Playfair Display',serif",
                        fontSize: '1.1rem',
                        fontWeight: 700,
                        color: N.navy,
                        mb: 1,
                        lineHeight: 1.35,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}>
                        {blog.title}
                      </Typography>
                      <Typography sx={{
                        fontSize: '0.85rem',
                        color: N.gray600,
                        lineHeight: 1.65,
                        mb: 1.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}>
                        {blog.description}
                      </Typography>
                      {(() => {
                        const dateVal = blog.publishedAt || blog.issueDate;
                        const formatted = formatDateDdMmYyyy(dateVal);
                        if (!formatted) return null;
                        return (
                          <Typography sx={{ fontSize: '0.75rem', color: N.muted, fontWeight: 500 }}>
                            {formatted}
                          </Typography>
                        );
                      })()}
                    </Box>
                  </Box>
                ))}
              </Box>
              </Box>
              {publicBlogsQuery.data.content.length > 3 && (
                <Box sx={{
                  position: 'absolute',
                  top: 0, right: 0, bottom: 0,
                  width: 60,
                  background: `linear-gradient(to right, transparent, ${N.white})`,
                  pointerEvents: 'none',
                  zIndex: 1,
                }} />
              )}
              </Box>
            )}
          </Reveal>
        </Box>
      </Box>

      {/* Blog detail modal */}
      <BlogDetailModal
        open={!!selectedBlogSlug}
        onClose={handleBlogModalClose}
        blog={blogDetailQuery.data}
        loading={blogDetailQuery.isLoading}
      />

      {/* ════════════════════════════ FOOTER ════════════════════════════════ */}
      <Box
        id="about"
        component="footer"
        sx={{
          background: N.gray50, py: { xs: '3rem', md: '4rem' },
          borderTop: `1px solid ${N.gray200}`,
        }}
      >
        <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, sm: 3, md: '4rem' } }}>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '2fr 1fr 1fr 1fr' },
            gap: { xs: 4, md: '4rem' }, mb: '3.5rem',
          }}>
            {/* Brand */}
            <Box>
              <Logo gradId="ft-g" width={160} height={34} variant="dark" />
              <Typography sx={{
                fontSize: '0.875rem', color: N.muted,
                lineHeight: 1.7, mt: 2, maxWidth: 280,
              }}>
                {t('landing_footer_description')}
              </Typography>
            </Box>
            {/* Link columns */}
            {[
              {
                title: t('landing_footer_modules'),
                links: [
                  t('landing_feature_admission_form'),
                  t('landing_feature_marksheet'),
                  t('landing_feature_admit_cards'),
                  t('landing_feature_student_management'),
                  t('landing_feature_aadhar'),
                ],
              },
              {
                title: t('landing_footer_company'),
                links: [t('landing_footer_link_about'), t('landing_footer_link_careers'), t('landing_footer_link_blog'), t('landing_footer_link_press'), t('landing_footer_link_contact')],
              },
              {
                title: t('landing_footer_resources'),
                links: [t('landing_footer_link_help'), t('landing_footer_link_implementation'), t('landing_footer_link_case_studies'), t('landing_footer_link_privacy'), t('landing_footer_link_terms')],
              },
            ].map(col => (
              <Box key={col.title}>
                <Typography sx={{
                  fontSize: '0.7rem', fontWeight: 600,
                  letterSpacing: '0.1em', textTransform: 'uppercase',
                  color: N.gray400, mb: 2,
                }}>
                  {col.title}
                </Typography>
                <Stack component="ul" spacing={1.5}
                  sx={{ listStyle: 'none', m: 0, p: 0 }}>
                  {col.links.map(link => (
                    <Box component="li" key={link}>
                      <Box component="a" sx={{
                        fontSize: '0.875rem', color: N.muted,
                        textDecoration: 'none', transition: 'color 0.2s',
                        '&:hover': { color: N.navy },
                      }}>
                        {link}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Box>
            ))}
          </Box>

          {/* Bottom bar */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            sx={{ pt: 3, borderTop: `1px solid ${N.gray200}`, gap: 1 }}
          >
            <Typography sx={{ fontSize: '0.8rem', color: N.gray400 }}>
              {t('landing_copyright', { year: new Date().getFullYear() })}
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: N.gray400 }}>
              {t('landing_footer_tagline')}
            </Typography>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default LandingPage;
